import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import * as sdk from '@cirqll/sdk';
import type { UserModule } from '~/types';
import { AUTH_URL, OAUTH_MOBILE_CLIENT_ID, OAUTH_MOBILE_REDIRECT_URI } from '~/constants';
import { useDevice } from '~/composable/use_device';
import { i18n } from './i18n';

/*
 * Native (iOS/Android) OAuth login, outside the WebView:
 *
 *   nativeLogin() → login() stores the request as `pending`
 *     → iOS: WebAuthSession sheet, which returns the redirect itself
 *     → otherwise: Browser.open(), the redirect returns via appUrlOpen (or the launch URL on a cold start)
 *   → answer() accepts only the redirect matching `pending`
 *   → complete() exchanges the code, stores the tokens, reloads the app.
 *
 * `loginInFlight` shares one login between all callers, and `reloading` stops new ones once a
 * reload is coming. `hydrated` gates everything until Keychain/Keystore has been read.
 */

interface WebAuthSessionPlugin {
    start(options: { url: string; callbackHost: string; callbackPath: string; callbackScheme: string }): Promise<{ url: string }>;
}

/** iOS-only, implemented in ios/App/App/WebAuthSessionPlugin.swift. */
const WebAuthSession = registerPlugin<WebAuthSessionPlugin>('WebAuthSession');

// Where public/oauth/mobile-callback.html forwards the redirect to when it reaches a browser: the
// sheet before iOS 17.4 can only catch a custom scheme. Registered in ios/App/App/Info.plist.
const CALLBACK_SCHEME = 'nl.cirqll.app';

// Keychain/Keystore rather than localStorage, which is unencrypted, and cleared by the app itself.
const ACCESS_TOKEN_KEY = 'cirqll_mobile_access_token';
const REFRESH_TOKEN_KEY = 'cirqll_mobile_refresh_token';
const PENDING_LOGIN_KEY = 'cirqll_mobile_pending_login';

/** The authorize request whose answer a login waits for. */
type PendingLogin = Pick<sdk.BuiltAuthorizeUrl, 'state' | 'verifier' | 'device'>;

let pending: PendingLogin | null = null;
let loginInFlight: Promise<void> | null = null;
let browserLoginDone: ((answered?: Promise<void>) => void) | null = null;
let reloading = false;
let hydrated: Promise<void> = Promise.resolve();

const completing = ref(false);

/** True from receiving the login redirect until the reload, or until the code exchange fails. */
export const isCompletingLogin = readonly(completing);

/** 'Cirqll Mobile' is its own Passport client (see PassportSeeder.php), separate from ~/modules/oauth.ts's web client — this flow never touches the sdk.configureOAuth() singleton, so both coexist in the same bundle. */
function mobileOAuthConfig(): sdk.OAuthConfig {
    return {
        clientId: OAUTH_MOBILE_CLIENT_ID,
        redirectUri: OAUTH_MOBILE_REDIRECT_URI,
        authorizeUrl: AUTH_URL,
        device: useDevice,
    };
}

async function readSecure(key: string): Promise<unknown> {
    try {
        return await SecureStorage.get(key);
    } catch {
        // Unreadable counts as absent.
        return null;
    }
}

async function readToken(key: string): Promise<string | null> {
    const value = await readSecure(key);

    return typeof value === 'string' ? value : null;
}

function isPendingLogin(value: unknown): value is PendingLogin {
    return typeof value === 'object' && value !== null
        && 'state' in value && typeof value.state === 'string'
        && 'verifier' in value && typeof value.verifier === 'string';
}

async function storeTokens(tokens: sdk.TokenExchangeResult): Promise<void> {
    await SecureStorage.set(ACCESS_TOKEN_KEY, tokens.access_token);

    if (tokens.refresh_token === undefined) {
        await SecureStorage.remove(REFRESH_TOKEN_KEY);
    } else {
        await SecureStorage.set(REFRESH_TOKEN_KEY, tokens.refresh_token);
    }
}

/** Best effort: one key failing to clear doesn't keep the other. */
async function removeSecure(...keys: string[]): Promise<void> {
    await Promise.all(keys.map(key => SecureStorage.remove(key).catch(() => undefined)));
}

async function clearSession(): Promise<void> {
    delete sdk.api.defaults.headers.common.Authorization;
    await removeSecure(ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY);
}

async function forgetPendingLogin(): Promise<void> {
    pending = null;
    await removeSecure(PENDING_LOGIN_KEY);
}

/**
 * How the SDK renews the native session after a 401 (registered with
 * sdk.configureAuthRefresh() in ~/modules/axios.ts), from the tokens kept in Keychain/Keystore.
 */
export function nativeRefreshStrategy(): sdk.RefreshStrategy {
    return sdk.bearerRefresh({
        getAccessToken: () => readToken(ACCESS_TOKEN_KEY),
        getRefreshToken: () => readToken(REFRESH_TOKEN_KEY),
        save: storeTokens,
    }, mobileOAuthConfig());
}

/**
 * Opens auth.cirqll.nl in the OS's browser, never the WebView (Google blocks WebView logins,
 * RFC 8252 advises against them). Resolves when the user is back: logged in, cancelled, or
 * shown an error.
 *
 * Always uses prompt=login, because the browser may still have an auth.cirqll.nl session
 * (Custom Tabs share Chrome's cookies). Without it:
 * - a logout would be followed by a silent login into the same account;
 * - a silent login on a second phone would make core's one-session-per-device limit log
 *   out the first.
 * Typing credentials also provides the user gesture iOS/Android need before handing the
 * redirect to the app.
 */
export async function nativeLogin(): Promise<void> {
    await hydrated;

    if (reloading) {
        return;
    }

    return loginInFlight ?? track(login());
}

/** Every 401 asks for a login (see onLoggedOut in ~/modules/axios.ts): they all get the one in flight. */
function track(login: Promise<void>): Promise<void> {
    loginInFlight = login.finally(() => {
        loginInFlight = null;
    });

    return loginInFlight;
}

async function login(): Promise<void> {
    try {
        const request = await sdk.buildAuthorizeUrlForRelay({ config: mobileOAuthConfig(), promptLogin: true });

        // Persisted as well: Android may kill the app behind the browser, whose redirect then
        // cold-starts it (see hydrate()).
        pending = { state: request.state, verifier: request.verifier, device: request.device };
        await SecureStorage.set(PENDING_LOGIN_KEY, pending);

        if (!await loginInSheet(request.url)) {
            await loginInBrowser(request.url);
        }
    } catch (error) {
        reportLoginFailure(error);
    }
}

/**
 * iOS: an ASWebAuthenticationSession sheet, which catches the redirect itself instead of leaving
 * it to a Universal Link — the HTTPS one from iOS 17.4, CALLBACK_SCHEME before. False where that's
 * unavailable, for the browser to take over.
 */
async function loginInSheet(url: string): Promise<boolean> {
    if (!Capacitor.isPluginAvailable('WebAuthSession')) {
        return false;
    }

    const redirect = new URL(OAUTH_MOBILE_REDIRECT_URI);
    let result: { url: string };

    try {
        result = await WebAuthSession.start({ url, callbackHost: redirect.host, callbackPath: redirect.pathname, callbackScheme: CALLBACK_SCHEME });
    } catch (error) {
        const code = (error as { code?: string }).code;

        // The sheet couldn't start.
        if (code === 'UNAVAILABLE') {
            return false;
        }

        // Unlike Android's browser, the sheet can't deliver a redirect after closing.
        if (code === 'CANCELED') {
            await forgetPendingLogin();

            return true;
        }

        throw error;
    }

    await handleCallback(result.url);

    return true;
}

/**
 * Custom Tabs (Android), or SFSafariViewController where the sheet couldn't start: the redirect
 * comes back through appUrlOpen, as an App Link/Universal Link or from the link the callback page
 * offers when that didn't open the app — or never, when the user closes the browser.
 */
async function loginInBrowser(url: string): Promise<void> {
    const done = new Promise<void>((resolve) => {
        browserLoginDone = resolve;
    });

    try {
        await Browser.open({ url });
        await done;
    } finally {
        browserLoginDone = null;
    }
}

/** Ends the login waiting on its browser, along with the handling of its answer if there is one. */
function finishBrowserLogin(answered?: Promise<void>): boolean {
    const done = browserLoginDone;
    browserLoginDone = null;
    done?.(answered);

    return done !== null;
}

/**
 * Where every way back from a login ends up: the sheet's result, an App Link/Universal Link,
 * or the URL that cold-started the app. Only the answer to the pending request gets through,
 * once; anything else is ignored, like a second delivery of that redirect, the launch URL iOS
 * hands out again after every reload, or a forged link.
 */
async function handleCallback(url: string): Promise<void> {
    await hydrated;
    await answer(url);
}

function answer(url: string): Promise<void> | undefined {
    const request = pending;
    // Rewrites nl.cirqll.app://… back to https://… so it matches OAUTH_MOBILE_REDIRECT_URI.
    const redirect = url.startsWith(`${CALLBACK_SCHEME}:`) ? `https:${url.slice(CALLBACK_SCHEME.length + 1)}` : url;
    const response = request === null ? null : sdk.parseAuthorizationResponse(redirect, { redirectUri: OAUTH_MOBILE_REDIRECT_URI, state: request.state });

    if (request === null || response === null) {
        return undefined;
    }

    // Claimed before anything async, so a second delivery stops above.
    pending = null;
    completing.value = true;

    const handling = complete(request, response);

    // With no login waiting on its browser (Android may report it closed before handing over the
    // redirect, and a cold start has none), this still counts as the login in flight.
    if (!finishBrowserLogin(handling) && loginInFlight === null) {
        void track(handling);
    }

    return handling;
}

async function complete(request: PendingLogin, response: sdk.AuthorizationResponse): Promise<void> {
    try {
        // Removed before reloading: iOS returns the same launch URL after a reload, which would match again.
        await SecureStorage.remove(PENDING_LOGIN_KEY);
        // Rejects when there's none open, as after the sheet, which closes itself.
        await Browser.close().catch(() => undefined);

        if ('error' in response) {
            throw response.error;
        }

        const tokens = await sdk.exchangeAuthorizationCode(response.code, mobileOAuthConfig(), request);
        await storeTokens(tokens);
    } catch (error) {
        completing.value = false;
        reportLoginFailure(error);
        return;
    }

    // App.vue's auth check is a one-shot at boot, so only a reboot reruns it with this token.
    reloading = true;
    window.location.reload();
}

function reportLoginFailure(error: unknown): void {
    // oxlint-disable-next-line no-console
    console.error('Login failed', error);
    // Lazily, as ~/utils/swal imports ~/utils, which imports this module.
    void import('~/utils/swal').then(({ swalNotification }) => swalNotification(i18n.global.t('login.failed'), true, 4000));
}

/** Core no longer accepts the stored session (see onLoggedOut in ~/modules/axios.ts). */
export async function nativeSessionEnded(): Promise<void> {
    await hydrated;

    // A login under way replaces the session anyway.
    if (reloading || loginInFlight !== null) {
        return;
    }

    await clearSession();
    await nativeLogin();
}

/**
 * Revokes the token server-side and clears the Keychain/Keystore, so a relaunch can't rehydrate
 * it, then reboots onto App.vue's login screen: its auth state is a one-shot check, not reactive.
 */
export async function nativeLogout(): Promise<void> {
    reloading = true;

    try {
        await sdk.logout();
    } finally {
        await clearSession();
        window.location.reload();
    }
}

async function hydrate(): Promise<void> {
    const [accessToken, storedPending, launch] = await Promise.all([
        readToken(ACCESS_TOKEN_KEY),
        readSecure(PENDING_LOGIN_KEY),
        App.getLaunchUrl().catch(() => undefined),
    ]);

    if (accessToken !== null) {
        sdk.api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }

    pending = isPendingLogin(storedPending) ? storedPending : null;

    // A cold start by the login's redirect, claimed before anything awaiting hydration can
    // start a login that replaces the pending one.
    if (launch !== undefined) {
        void answer(launch.url);
    }
}

/**
 * Awaited by fetchCurrentUser() (~/utils/auth.ts) before its first auth check, which would
 * otherwise race SecureStorage and take a session from a previous app run for logged out.
 */
export async function waitForTokenHydration(): Promise<void> {
    await hydrated;
}

export const install: UserModule = () => {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    hydrated = hydrate();

    void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        void handleCallback(event.url);
    });
    void Browser.addListener('browserFinished', () => {
        finishBrowserLogin();
    });
};
